import React from "react"
import cx from "classnames"
import {useTranslation} from "react-i18next"
import {getFullName} from "helpers"
import {useDateTimeFormatters} from "hooks"
import {Icon} from "components/Icon"
import {StudentChecklistItem, StudentChecklistStatus} from "types/students"
import styles from "./StudentDepartmentChecklistItem.module.css"

type Props = {
    item: StudentChecklistItem
}

const STATUS_LABEL_KEYS: Record<StudentChecklistStatus, string> = {
    pending: "checklist.pending",
    approved: "checklist.approved",
    rejected: "checklist.rejected"
}

export function StudentDepartmentChecklistItem(props: Props) {
    const {item} = props
    const {t} = useTranslation(["common"])
    const {formatDate, formatDateTime} = useDateTimeFormatters()
    const name = item.name || item.checklistItem?.name

    const renderStatusValue = (
        status: StudentChecklistStatus,
        signature: StudentChecklistItem["approvalSignature"],
        updatedAt: string | null
    ) => {
        if (status === "approved") {
            return (
                <div className={cx(styles.rowFieldValue, styles.rowStatusValue, styles.rowCompletedValue)}>
                    <div className={styles.rowStatusIcon}>
                        <Icon icon="CHECKMARK_CIRCLE" color="#18A957" className={styles.rowActivityIcon} />
                    </div>
                    <div className={styles.rowStatusValue}>
                        {updatedAt ? formatDateTime(updatedAt) : "-"}
                        <br />
                        {t("action.by")} {getFullName(signature) || t("message.system")}
                    </div>
                </div>
            )
        }

        if (status === "rejected") {
            return (
                <div className={cx(styles.rowFieldValue, styles.rowStatusValue, styles.rowRejectedValue)}>
                    <div className={styles.rowStatusIcon}>
                        <Icon icon="CROSS_CIRCLE" color="#DF1642" className={styles.rowActivityIcon} />
                    </div>
                    <div className={styles.rowStatusValue}>
                        {updatedAt ? formatDateTime(updatedAt) : "-"}
                        <br />
                        {t("action.by")} {getFullName(signature) || t("message.system")}
                    </div>
                </div>
            )
        }

        return <div className={styles.rowFieldValue}>{t(STATUS_LABEL_KEYS[status] ?? "checklist.pending")}</div>
    }

    return (
        <div className={styles.rowWrap}>
            <div className={styles.rowTitleWrap}>
                <div className={styles.itemName} title={name}>
                    {name}
                </div>
                <div className="flex-1" />
                <span className={cx(styles.requiredBadge, {[styles.requiredBadge__optional]: !item.isRequired})}>
                    {item.isRequired ? t("checklist.required") : t("checklist.notRequired")}
                </span>
            </div>
            <div className={styles.rowFieldWrap}>
                <div className={styles.rowFieldTitle}>{t("checklist.approval")}</div>
                {renderStatusValue(item.approvalStatus, item.approvalSignature, item.approvalStatusUpdatedAt)}
            </div>
            <div className={styles.rowFieldWrap}>
                <div className={styles.rowFieldTitle}>{t("checklist.audit")}</div>
                {renderStatusValue(item.auditStatus, item.auditSignature, item.auditStatusUpdatedAt)}
            </div>
            {item.dueDate && (
                <div className={styles.rowFieldWrap}>
                    <div className={styles.rowFieldTitle}>{t("checklist.dueDate")}</div>
                    <div className={styles.rowFieldValue}>{formatDate(item.dueDate)}</div>
                </div>
            )}
            {item.checklistItem?.isDocumentUploadRequired && (
                <div className={styles.rowFieldWrap}>
                    <div className={styles.rowFieldTitle}>{t("checklist.documentUpload")}</div>
                    <div className={styles.rowFieldValue}>{t("checklist.required")}</div>
                </div>
            )}
        </div>
    )
}
